import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {CommerceTransactionsWorkspaceCapabilityUdoTemplateKey} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/commerceTransactionsWorkspaceCapability/domain/CommerceTransactionsWorkspaceCapabilityUdoTemplateKey";
import type {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import type {WorkspaceCapabilityUdoTemplateConfiguration} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityUdoTemplateConfiguration";
import {z as zod} from "zod";

export type CommerceTransactionsWorkspaceCapabilityUdoBindings = WorkspaceCapabilityUdoTemplateConfiguration<WorkspaceCapabilityType.commerceTransactions> & {
    fixedUdoTemplates: {
        [CommerceTransactionsWorkspaceCapabilityUdoTemplateKey.coupon]: {
            udoAdditionalColumnIds: {
                code: Uuid;
                discountType: Uuid;
                discountValue: Uuid;
                expiresAt: Uuid;
                isActive: Uuid;
                minOrderValue: Uuid;
                status: Uuid;
                timesUsed: Uuid;
                usageLimit: Uuid;
            };
        };
        [CommerceTransactionsWorkspaceCapabilityUdoTemplateKey.cart]: {
            udoAdditionalColumnIds: {
                couponId: Uuid;
                ottokartStorefrontCustomerId: Uuid;
                status: Uuid;
            };
        };
        [CommerceTransactionsWorkspaceCapabilityUdoTemplateKey.cartItem]: {
            udoAdditionalColumnIds: {
                cartId: Uuid;
                productCatalogueId: Uuid;
                quantity: Uuid;
                status: Uuid;
            };
        };
        [CommerceTransactionsWorkspaceCapabilityUdoTemplateKey.order]: {
            udoAdditionalColumnIds: {
                billingAddressSnapshot: Uuid;
                orderNumber: Uuid;
                ottokartStorefrontCustomerId: Uuid;
                paymentMethod: Uuid;
                paymentStatus: Uuid;
                purchaseOrderNumber: Uuid;
                shippingAddressSnapshot: Uuid;
                shippingAmount: Uuid;
                status: Uuid;
                taxAmount: Uuid;
                totalAmount: Uuid;
            };
        };
        [CommerceTransactionsWorkspaceCapabilityUdoTemplateKey.orderItem]: {
            udoAdditionalColumnIds: {
                discountAmount: Uuid;
                orderId: Uuid;
                productCatalogueId: Uuid;
                productNameSnapshot: Uuid;
                purchasePrice: Uuid;
                quantity: Uuid;
                skuSnapshot: Uuid;
                status: Uuid;
                taxAmount: Uuid;
            };
        };
        [CommerceTransactionsWorkspaceCapabilityUdoTemplateKey.invoice]: {
            udoAdditionalColumnIds: {
                amountDue: Uuid;
                amountPaid: Uuid;
                dueDate: Uuid;
                invoiceNumber: Uuid;
                issuedAt: Uuid;
                orderId: Uuid;
                paidAt: Uuid;
                status: Uuid;
            };
        };
    };
};

export const commerceTransactionsWorkspaceCapabilityUdoBindingsValidator = zod.strictObject({
    fixedUdoTemplates: zod.strictObject({
        [CommerceTransactionsWorkspaceCapabilityUdoTemplateKey.coupon]: zod.strictObject({
            udoTemplateId: uuidZodValidator,
            udoAdditionalColumnIds: zod.strictObject({
                status: uuidZodValidator,
                code: uuidZodValidator,
                discountType: uuidZodValidator,
                discountValue: uuidZodValidator,
                minOrderValue: uuidZodValidator,
                usageLimit: uuidZodValidator,
                timesUsed: uuidZodValidator,
                expiresAt: uuidZodValidator,
                isActive: uuidZodValidator,
            }),
        }),
        [CommerceTransactionsWorkspaceCapabilityUdoTemplateKey.cart]: zod.strictObject({
            udoTemplateId: uuidZodValidator,
            udoAdditionalColumnIds: zod.strictObject({status: uuidZodValidator, ottokartStorefrontCustomerId: uuidZodValidator, couponId: uuidZodValidator}),
        }),
        [CommerceTransactionsWorkspaceCapabilityUdoTemplateKey.cartItem]: zod.strictObject({
            udoTemplateId: uuidZodValidator,
            udoAdditionalColumnIds: zod.strictObject({status: uuidZodValidator, cartId: uuidZodValidator, productCatalogueId: uuidZodValidator, quantity: uuidZodValidator}),
        }),
        [CommerceTransactionsWorkspaceCapabilityUdoTemplateKey.order]: zod.strictObject({
            udoTemplateId: uuidZodValidator,
            // Temporary local compatibility: staging may contain legacy order mappings
            // that are not part of the current Commerce Order type. Zod's default object
            // behavior strips those unknown keys while preserving validation of fields
            // defined below. Keep the persisted staging data cleanup as the real fix.
            udoAdditionalColumnIds: zod.object({
                status: uuidZodValidator,
                orderNumber: uuidZodValidator,
                ottokartStorefrontCustomerId: uuidZodValidator,
                totalAmount: uuidZodValidator,
                taxAmount: uuidZodValidator,
                shippingAmount: uuidZodValidator,
                shippingAddressSnapshot: uuidZodValidator,
                billingAddressSnapshot: uuidZodValidator,
                purchaseOrderNumber: uuidZodValidator,
                paymentMethod: uuidZodValidator,
                paymentStatus: uuidZodValidator,
            }),
        }),
        [CommerceTransactionsWorkspaceCapabilityUdoTemplateKey.orderItem]: zod.strictObject({
            udoTemplateId: uuidZodValidator,
            udoAdditionalColumnIds: zod.strictObject({
                status: uuidZodValidator,
                orderId: uuidZodValidator,
                productCatalogueId: uuidZodValidator,
                skuSnapshot: uuidZodValidator,
                productNameSnapshot: uuidZodValidator,
                purchasePrice: uuidZodValidator,
                quantity: uuidZodValidator,
                taxAmount: uuidZodValidator,
                discountAmount: uuidZodValidator,
            }),
        }),
        [CommerceTransactionsWorkspaceCapabilityUdoTemplateKey.invoice]: zod.strictObject({
            udoTemplateId: uuidZodValidator,
            udoAdditionalColumnIds: zod.strictObject({
                status: uuidZodValidator,
                orderId: uuidZodValidator,
                invoiceNumber: uuidZodValidator,
                amountDue: uuidZodValidator,
                amountPaid: uuidZodValidator,
                issuedAt: uuidZodValidator,
                dueDate: uuidZodValidator,
                paidAt: uuidZodValidator,
            }),
        }),
    }),
}) satisfies zod.ZodType<CommerceTransactionsWorkspaceCapabilityUdoBindings>;
